import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { CardContent } from 'components/ui/card';
import { ChartConfig, ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from 'components/ui/chart';
import styled from 'styled-components';
import { Accent, BorderColor } from 'styles/Global/colorSemantic';
import { Area, CartesianGrid, XAxis, YAxis, Line, ComposedChart, ReferenceLine } from 'recharts';
import { Radius } from 'styles/Global/objectStyles';
import CLPatientInfoTable from './CLPatientInfoTable';
import { Body5Medium, Title2Bold } from 'styles/Global/typography';
import { handleDate } from '../utils';
import useToggle from 'hooks/CustomHooks/useToggle';
import DropdownButton from 'components/Buttons/DropdownButton';
import { CLPatientInfoChartFilterList } from '../constants';
import IconArrowUp from 'assets/Icons/Arrow/Icon_arrow_up';
import SearchFilterDropdownNotPeriod from 'components/Dropdown/SearchFilterDropdownNotPeriod';
import IconArrowDown from 'assets/Icons/Arrow/Icon_arrow_down';
import { ResetIconWrapper } from 'styles/Input/inputStyle';
import RefreshIcon from 'assets/Icons/Common/RefreshIcon';
import { GetStatisticsByDateApiData } from 'models/CL/CLApiTypes';
import { ICLDailyStatistics } from 'models/CL/CLBasicInterface';
import moment from 'moment';

const chartConfig = {
    foodCalories: {
        label: '음식섭취칼로리',
        color: 'hsl(var(--chart-1))',
    },
    exerciseCalories: {
        label: '운동소모칼로리',
        color: 'hsl(var(--chart-2))',
    },
    targetCalories: {
        label: '목표 섭취 칼로리',
        // color: '#ff6b6b',
    },
    targetExerciseCalories: {
        label: '목표 운동소모 칼로리',
        // color: '#4ecdc4',
    },
} satisfies ChartConfig;

interface CLCaloriesInfoProps {
    selectFilter: number;
    setselectFilter: Dispatch<SetStateAction<number>>;
    statisticsData: ICLDailyStatistics[];
    startDate: string;
    endDate: string;
}

const CLCaloriesInfo = ({ selectFilter, setselectFilter, statisticsData, startDate, endDate }: CLCaloriesInfoProps) => {
    const [isFilterDropdown, setIsFilterDropdown] = useToggle();

    const dropdownRef = useRef<HTMLButtonElement>(null);

    statisticsData = statisticsData.sort((a, b) => {
        return moment(a.date, 'YYYYMMDD').valueOf() - moment(b.date, 'YYYYMMDD').valueOf();
    });

    useEffect(() => {
        const handleClickOutside = (event: any) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) && isFilterDropdown) {
                setIsFilterDropdown();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isFilterDropdown]);

    const handleDropdown = (e: React.MouseEvent, idx: number) => {
        e.stopPropagation();
        setIsFilterDropdown();
        setselectFilter(idx);
    };

    // 안전한 데이터 처리
    const safeStatisticsData = statisticsData || [];

    const filteredData = safeStatisticsData.filter((item) => {
        if (!item.date) return false;

        try {
            // YYYYMMDD 포맷을 YYYY-MM-DD로 변환하여 파싱
            const year = item.date.slice(0, 4);
            const month = item.date.slice(4, 6);
            const day = item.date.slice(6, 8);
            const date = new Date(`${year}-${month}-${day}`);

            const start = new Date(startDate);
            const end = new Date(endDate);

            return date >= start && date <= end;
        } catch (error) {
            console.error('Date parsing error:', error, item.date);
            return false;
        }
    });

    // 동적 Y축 범위 계산 - 안전한 처리
    const maxValue =
        filteredData.length > 0
            ? Math.max(...filteredData.map((item) => Math.max(item.foodCalories || 0, item.exerciseCalories || 0, item.targetCalories || 0, item.targetExerciseCalories || 0)))
            : 1000; // 기본값

    // 동적 Y축 간격 계산 함수
    const calculateYAxisInterval = (maxValue: number) => {
        if (maxValue <= 1000) return 200;
        if (maxValue <= 2000) return 400;
        if (maxValue <= 5000) return 500;
        if (maxValue <= 10000) return 1000;
        return 2000;
    };

    // Y축 설정
    const yAxisInterval = calculateYAxisInterval(maxValue);
    const yAxisMax = Math.ceil(maxValue / yAxisInterval) * yAxisInterval;

    // Y축 tick을 동적 간격으로 생성
    const yAxisTicks = [];
    for (let i = 0; i <= yAxisMax; i += yAxisInterval) {
        yAxisTicks.push(i);
    }

    // 차트용 데이터 변환 - 날짜 포맷 변경
    const chartData = filteredData.map((item) => {
        const year = item.date.slice(0, 4);
        const month = item.date.slice(4, 6);
        const day = item.date.slice(6, 8);

        return {
            ...item,
            date: `${year}-${month}-${day}`,
            // 숫자 값 보장
            foodCalories: Number(item.foodCalories) || 0,
            exerciseCalories: Number(item.exerciseCalories) || 0,
            targetCalories: Number(item.targetCalories) || 0,
            targetExerciseCalories: Number(item.targetExerciseCalories) || 0,
        };
    });

    return (
        <>
            <ChartFilterContainer>
                {handleDate(selectFilter).date}
                <FilterButtonWrapper>
                    <DropdownButton onClick={setIsFilterDropdown} ref={dropdownRef} $width='132px'>
                        {CLPatientInfoChartFilterList[selectFilter]}
                        <DropdownIconWrapper>
                            {isFilterDropdown ? (
                                <>
                                    <IconArrowUp />
                                    <SearchFilterDropdownNotPeriod type='CLPatientInfoChartFilterList' selectedSearchFilter={selectFilter} handleDropdown={handleDropdown} />
                                </>
                            ) : (
                                <IconArrowDown />
                            )}
                        </DropdownIconWrapper>
                    </DropdownButton>
                    <ResetIconWrapper onClick={() => window.location.reload()}>
                        <RefreshIcon />
                    </ResetIconWrapper>
                </FilterButtonWrapper>
            </ChartFilterContainer>
            <ChartBox>
                <CardContent className='h-full' style={{ padding: '28px 40px 32px 32px' }}>
                    {chartData.length === 0 ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <p>표시할 데이터가 없습니다.</p>
                        </div>
                    ) : (
                        <ChartContainer config={chartConfig} className='aspect-auto h-full w-full'>
                            <ComposedChart data={chartData}>
                                <defs>
                                    <linearGradient id='fillFoodCalories' x1='0' y1='0' x2='0' y2='1'>
                                        <stop offset='5%' stopColor='var(--color-foodCalories)' stopOpacity={0.8} />
                                        <stop offset='95%' stopColor='var(--color-foodCalories)' stopOpacity={0.1} />
                                    </linearGradient>
                                    <linearGradient id='fillExerciseCalories' x1='0' y1='0' x2='0' y2='1'>
                                        <stop offset='5%' stopColor='var(--color-exerciseCalories)' stopOpacity={0.8} />
                                        <stop offset='95%' stopColor='var(--color-exerciseCalories)' stopOpacity={0.1} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid vertical={false} horizontal={false} />

                                {/* Y축 200 단위에 맞춘 그리드 라인 */}
                                {yAxisTicks.map(
                                    (tick, index) =>
                                        index > 0 && ( // 0은 제외 (차트 하단선)
                                            <ReferenceLine key={tick} y={tick} stroke='#e0e0e0' strokeDasharray='0' strokeWidth={1} />
                                        )
                                )}

                                <XAxis
                                    dataKey='date'
                                    tickLine={false}
                                    axisLine={false}
                                    tickMargin={8}
                                    minTickGap={32}
                                    tickFormatter={(value) => {
                                        const date = new Date(value);
                                        const month = date.getMonth() + 1;
                                        const day = date.getDate();
                                        return `${month}월 ${day}일`;
                                    }}
                                />
                                <YAxis tickLine={false} axisLine={false} tickMargin={8} domain={[0, yAxisMax]} ticks={yAxisTicks} />
                                <ChartTooltip
                                    cursor={false}
                                    content={
                                        <ChartTooltipContent
                                            labelFormatter={(value) => {
                                                const date = new Date(value);
                                                const year = date.getFullYear();
                                                const month = date.getMonth() + 1;
                                                const day = date.getDate();
                                                return `${year}년 ${month}월 ${day}일`;
                                            }}
                                            indicator='dot'
                                        />
                                    }
                                />
                                <Area dataKey='foodCalories' type='monotone' fill='url(#fillFoodCalories)' stroke='var(--color-foodCalories)' />
                                <Area dataKey='exerciseCalories' type='monotone' fill='url(#fillExerciseCalories)' stroke='var(--color-exerciseCalories)' />

                                {/* 목표 섭취 칼로리 기준선 */}
                                <Line dataKey='targetCalories' stroke={Accent.color_accent_yellow_heavy} strokeWidth={1} dot={false} type='step' strokeDasharray='5 5' />

                                {/* 목표 운동소모 칼로리 기준선 */}
                                <Line dataKey='targetExerciseCalories' stroke={Accent.color_accent_lime_heavy} strokeWidth={1} dot={false} type='step' strokeDasharray='5 5' />

                                {/* <ChartLegend content={<ChartLegendContent />} /> */}
                                <ChartLegend
                                    content={(props) => {
                                        const { payload } = props;
                                        if (!payload?.length) return null;

                                        // targetCalories와 targetExerciseCalories 제외
                                        const filteredPayload = payload.filter((item) => item.dataKey !== 'targetCalories' && item.dataKey !== 'targetExerciseCalories');

                                        return <ChartLegendContent payload={filteredPayload} />;
                                    }}
                                />
                            </ComposedChart>
                        </ChartContainer>
                    )}
                </CardContent>
            </ChartBox>
            <CLPatientInfoTable currentData={statisticsData} />
        </>
    );
};

export default CLCaloriesInfo;

const ChartBox = styled.div`
    width: 100%;
    height: 358px;
    border: 1px solid ${BorderColor.color_border_neutral};
    border-radius: ${Radius.radius_4};
    margin-bottom: 32px;
`;

const ChartFilterContainer = styled(Title2Bold)`
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
`;

const FilterButtonWrapper = styled.div`
    display: flex;
    align-items: center;
    height: 100%;
`;

const DropdownIconWrapper = styled(Body5Medium)`
    display: flex;
    align-items: center;
    height: 100%;
`;
